import React, {
	useState,
	useEffect,
	useContext,
	useCallback,
	useMemo,
} from 'react';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import Loading from 'tbk-components/src/components/Loading';
import { ICareersProfileModalI18n } from '../CareersProfileForm';
import CareersProfileCard, {
	ICareersProfileCardI18n,
} from '../CareersProfileCard';
import CareersProfileProgressCard, {
	ICareersProfileProgressCardI18n,
} from '../CareersProfileProgressCard';
import { IImage } from 'tbk-components/src/components/Image';
import Button, { ILink } from 'tbk-components/src/components/Button';
import DashboardCard, {
	IDashboardCardI18n,
	IDashboardCareerCardProps,
} from './DashboardCard';
import CareersDashboardHeadingFilter, {
	ICareersDashboardHeadingFilterProps,
} from '../CareersDashboardHeadingFilter';
import { IDashboardCardModalI18n } from './DashboardCardModal';
import { orderBy } from 'lodash';
import DashboardFilters, { IDashboardFiltersI18n } from './DashboardFilters';
import { useHashAsState } from 'tbk-components/src/hooks';
import { ICareersFilterProps } from '../CareerCardGroup';
import Modal from 'tbk-components/src/components/Modal';
import { AnimationOnScroll } from 'react-animation-on-scroll';

interface IApplicantDashboardAssets {
	i18n: {
		dashboardI18n: {
			noCareersMatchYourFilters: string;
			resetAllFilters: string;
			deleteAccount?: {
				link: string;
				confirmation: string; // html allowed
				continue: string;
				cancel: string;
			};
		};
		modalI18n: ICareersProfileModalI18n;
		profileCardI18n: ICareersProfileCardI18n;
		profileProgressCardI18n: ICareersProfileProgressCardI18n;
		dashboardHeaderI18n: ICareersDashboardHeadingFilterProps;
		dashboardCardI18n: IDashboardCardI18n;
		dashboardCardModalI18n: IDashboardCardModalI18n;
		dashboardFiltersI18n: IDashboardFiltersI18n;
	};
	profileImage?: IImage;
	profileName?: string;
	profileTitle?: string;
	progressAmount?: number;
	careerCards?: IDashboardCareerCardProps[];
	resetPassword?: ILink;
	unauthorized?: {
		heading: string;
		message: string;
		link: ILink;
	};
}

export interface IApplicantDashboardProps {
	/**
	 * @noUI
	 */
	context?: any;
	/**
	 * @noUI
	 */
	defaultAssets?: IApplicantDashboardAssets;
}

/**
 * Drives the applicant dashboard page.
 */
const ApplicantDashboard: React.FC<IApplicantDashboardProps> = withAppContext(
	({ context, defaultAssets }) => {
		const { Api } = useContext<any>(context);
		const [assets, setAssets] = useState<IApplicantDashboardAssets>(null);
		const [cardsToDisplay, setCardsToDisplay] = useState<
			IDashboardCareerCardProps[]
		>([]);
		const filterHash = JSON.stringify(
			assets?.i18n?.dashboardFiltersI18n?.filters || [],
		);
		const dynamicParameters = useMemo(() => {
			const filters = JSON.parse(filterHash);
			const dynamicParameters = {};
			(filters || []).forEach((filter: ICareersFilterProps) => {
				dynamicParameters[filter.taxonomy] = 'csv';
			});
			return dynamicParameters;
		}, [filterHash]);

		const [state, normalizedState, updateState] = useHashAsState({
			parameters: {
				...dynamicParameters,
			},
			defaults: {},
		});
		const [showModal, setShowModal] = useState<string>(null);
		const [busy, setBusy] = useState<boolean>(false);

		// Lodash's orderBy makes this easy.  the function for the hasBeenDismissed is in case it's undefined.
		const cardSorter = useCallback((cards: IDashboardCareerCardProps[]) => {
			return orderBy(
				cards,
				[
					'allTerms.status.0',
					'fullApplicationSubmitted',
					(card) => !!card.hasBeenDismissed,
					'careerPostingDate',
				],
				['desc', 'asc', 'asc', 'desc'],
			);
		}, []);

		useEffect(() => {
			if (defaultAssets) {
				setAssets({
					...defaultAssets,
					careerCards: cardSorter(defaultAssets.careerCards),
				});
			} else {
				Api.getApplicantDashboardAssets().then((assets) =>
					setAssets({
						...assets,
						careerCards: cardSorter(assets.careerCards),
					}),
				);
			}
		}, [defaultAssets, cardSorter]);
		const serializedState = JSON.stringify(state);
		useEffect(() => {
			if (!assets?.careerCards) {
				return;
			}

			const state = JSON.parse(serializedState);

			if (!Object.entries(state).length) {
				setCardsToDisplay(assets.careerCards);
			} else {
				setCardsToDisplay(
					assets.careerCards.filter((card) => {
						return (
							Object.entries(state).filter(
								([taxonomy, terms]) => {
									let pass = false;
									switch (taxonomy) {
										case 'application':
											(terms as string[]).forEach(
												(term: string) => {
													if (card[term]) {
														pass = true;
													}
												},
											);
											break;
										case 'job_status':
											(terms as string[]).forEach(
												(term: string) => {
													if (
														card.allTerms
															.status[0] === term
													) {
														pass = true;
													}
												},
											);
											break;
										default:
											break;
									}
									return pass;
								},
							).length === Object.entries(state).length
						);
					}),
				);
			}
		}, [serializedState, assets?.careerCards]);

		if (!assets) {
			return (
				<div className="container-fluid bg-light py-5 text-center">
					<Loading />
				</div>
			);
		} else if (assets.unauthorized) {
			return (
				<div className="container-fluid bg-light py-5 text-center">
					<h1>{assets.unauthorized.heading}</h1>
					<p>{assets.unauthorized.message}</p>
					<Button {...assets.unauthorized.link} />
				</div>
			);
		}

		const onDismissed = (ID: number) => {
			const newCards = assets.careerCards.map((card) => {
				if (card.ID === ID) {
					card.hasBeenDismissed = true;
				}
				return card;
			});
			setAssets({
				...assets,
				careerCards: cardSorter(newCards),
			});
			Api.postCareerManualAction({
				ID,
				target: 'dismissed_careers',
				action: 'add',
			});
		};

		const onManuallyCompleted = (ID: number) => {
			const newCards = assets.careerCards.map((card) => {
				if (card.ID === ID) {
					card.fullApplicationSubmitted = true;
				}
				return card;
			});
			setAssets({
				...assets,
				careerCards: cardSorter(newCards),
			});
			Api.postCareerManualAction({
				ID,
				target: 'manually_completed_applications',
				action: 'add',
			});
		};

		/**
		 * Gets called when user updates a filter ( which could be a select, multiple checkboxes or a single checkbox )
		 *
		 * @param e
		 */
		const onChangeFilter = (e: any) => {
			const input = e.target as HTMLInputElement;
			const parameter = input.name.replace(/^([a-z0-9\-_]+).*$/, '$1');
			const { value } = input;
			const changeset = {};

			if (input.tagName === 'SELECT') {
				// it's a select, or a single checkbox, just a single value
				changeset[parameter] = [value];
			} else if (input.name.match(/\[/)) {
				// it's a set of checkboxes - multiple values
				const values = state[parameter] || [];
				const index = values.indexOf(value);
				// @ts-ignore
				if (e.target.checked && index === -1) {
					values.push(value);
					// @ts-ignore
				} else if (!e.target.checked && index !== -1) {
					values.splice(index, 1);
				}
				changeset[parameter] = values;
			} else {
				// it's a single value
				changeset[parameter] = input.checked ? value : null;
			}

			updateState(changeset);
		};

		const onClearFilters = () => {
			const changeset = {};
			Object.keys(dynamicParameters).forEach((parameter: string) => {
				changeset[parameter] = null;
			});
			updateState(changeset);
		};

		const onDeleteAccount = () => {
			setBusy(true);
			Api.deleteCareersUser().then((response) => {
				if (response.redirect) {
					window.location.href = response.redirect;
				} else {
					window.location.reload();
				}
			});
		};

		return (
			<div className="calian-careers-dashboard bg-light-bg pt-2 pt-sm-6 pt-lg-10">
				<div className="calian-careers-dashboard__container container-fluid mt-1 mb-0 pb-1 mt-sm-5 mb-sm-0 pb-sm-5 pb-lg-10">
					<div className="row">
						<div className="col-12 col-lg-4 px-2 px-lg-3">
							<CareersProfileCard
								i18n={assets.i18n.profileCardI18n}
								profileImage={assets.profileImage}
								profileName={assets.profileName}
								profileTitle={assets.profileTitle}
								modalI18n={assets.i18n.modalI18n}
								classNames={['bg-transparent']}
							/>
							<CareersProfileProgressCard
								i18n={assets.i18n.profileProgressCardI18n}
								progressAmount={assets.progressAmount}
								modalI18n={assets.i18n.modalI18n}
							/>

							{!!assets?.careerCards.length && (
								<DashboardFilters
									i18n={assets.i18n.dashboardFiltersI18n}
									// @ts-ignore
									onChange={onChangeFilter}
									// @ts-ignore
									onClear={onClearFilters}
									state={state}
								/>
							)}
							<div className="reset-delete-buttons px-3 py-2 p-sm-3 px-lg-5 py-lg-4 card bg-transparent border-0 d-flex flex-row justify-content-start align-items-start">
								{assets.resetPassword && (
									<Button
										{...assets.resetPassword}
										size="sm"
										className={
											'btn btn-link mb-3 me-3 p-0 bg-transparent fs-8 text-decoration-none'
										}
									/>
								)}
								{assets.i18n.dashboardI18n.deleteAccount && (
									<Button
										onClick={() => setShowModal('delete')}
										label={
											assets.i18n.dashboardI18n
												.deleteAccount.link
										}
										size="sm"
										className={
											'btn btn-link mb-3 me-2 p-0 bg-transparent fs-8 text-decoration-none'
										}
									/>
								)}
							</div>
						</div>
						<div className="col-12 col-lg-8 px-0 px-lg-3">
							<CareersDashboardHeadingFilter
								{...assets.i18n.dashboardHeaderI18n}
							/>
							<div className="container-fluid">
								<div className="row">
									{cardsToDisplay.map((career, index) => (
										<DashboardCard
											key={career.ID}
											card={career}
											i18n={assets.i18n.dashboardCardI18n}
											modalI18n={
												assets.i18n
													.dashboardCardModalI18n
											}
											onDismissed={onDismissed}
											onManuallyCompleted={
												onManuallyCompleted
											}
											animationDelay={
												index === 0
													? 100
													: Math.min(
															100 + index * 50,
															250,
													  )
											}
										/>
									))}

									{!cardsToDisplay.length &&
										!!assets?.careerCards.length && (
											<p className="text-center">
												{
													assets.i18n.dashboardI18n
														.noCareersMatchYourFilters
												}
												<Button
													size="sm"
													className="btn btn-link text-decoration-none bg-transparent border-0 p-0 pb-1 ps-2"
													label={
														assets.i18n
															.dashboardI18n
															.resetAllFilters
													}
													onClick={onClearFilters}
												/>
											</p>
										)}
								</div>
							</div>
						</div>
					</div>
				</div>
				{showModal === 'delete' && (
					<Modal
						onCancel={() => setShowModal(null)}
						onSubmit={() => onDeleteAccount()}
						title={assets.i18n.dashboardI18n.deleteAccount.link}
						className="calian-careers-profile-form"
						modalClassNames={['modal-xl']}
						headerClassNames={['p-lg-5 border-0']}
						bodyClassNames={['py-2 px-2 px-lg-5']}
						i18n={{
							cancel: assets.i18n.dashboardI18n.deleteAccount
								.cancel,
							submit: assets.i18n.dashboardI18n.deleteAccount
								.continue,
						}}
						showCancel
						showSubmit
						busy={busy}
					>
						<div
							dangerouslySetInnerHTML={{
								__html: assets.i18n.dashboardI18n.deleteAccount
									.confirmation,
							}}
						/>
					</Modal>
				)}
			</div>
		);
	},
);

export default ApplicantDashboard;
